import type { CSSProperties } from 'react';

interface SkProps {
  style?: CSSProperties;
  className?: string;
}

export const Sk = ({ style, className = '' }: SkProps) => (
  <span className={`sk ${className}`} style={style} aria-hidden="true" />
);

const widthPct = (seed: number, min: number, max: number) => {
  const factor = (((seed * 73) % 41) + ((seed * 29) % 17)) / 57;
  return Math.round(min + factor * (max - min));
};

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  label?: string;
}

export const TableSkeleton = ({ rows = 8, columns = 5, label = 'Loading data' }: TableSkeletonProps) => {
  const columnWidths = Array.from({ length: columns }, (_, column) => widthPct(column + 2, 42, 88));
  return (
    <div className="table-scroll skeleton-region" role="status" aria-busy="true" aria-label={label}>
      <table className="skeleton-table">
        <thead>
          <tr>
            {columnWidths.map((width, column) => (
              <th key={column}>
                <Sk style={{ width: `${Math.min(width - 22, 62)}%`, minWidth: 42 }} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            <tr key={row}>
              {columnWidths.map((width, column) => (
                <td key={column}>
                  <Sk style={{ width: `${column === 0 ? Math.min(width + 10, 96) : width}%`, minWidth: 38 }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface LinesSkeletonProps {
  lines?: number;
  className?: string;
  label?: string;
}

export const LinesSkeleton = ({ lines = 3, className = '', label = 'Loading' }: LinesSkeletonProps) => (
  <div className={`sk-lines ${className}`} role="status" aria-busy="true" aria-label={label}>
    {Array.from({ length: lines }, (_, index) => (
      <Sk key={index} className={index === lines - 1 ? 'sk-line short' : 'sk-line'} />
    ))}
  </div>
);

interface ChartSkeletonProps {
  height?: number;
  bars?: number;
}

export const ChartSkeleton = ({ height = 260, bars = 6 }: ChartSkeletonProps) => (
  <div className="chart-sketch skeleton-region" style={{ height }} role="status" aria-busy="true" aria-label="Loading chart">
    <span className="chart-sketch-axis" aria-hidden="true" />
    {Array.from({ length: bars }, (_, index) => {
      const seed = index + 3;
      const tall = widthPct(seed, 30, 92);
      const short = widthPct(seed + 11, 18, tall - 12);
      return (
        <span key={index} className="chart-sketch-group" aria-hidden="true">
          <Sk className="chart-sketch-bar muted" style={{ height: `${short}%` }} />
          <Sk className="chart-sketch-bar" style={{ height: `${tall}%` }} />
        </span>
      );
    })}
  </div>
);

export const DetailSkeleton = ({ label = 'Loading details' }: { label?: string }) => (
  <div className="detail-sketch skeleton-region" role="status" aria-busy="true" aria-label={label}>
    <Sk className="sk-heading" />
    <div className="detail-sketch-grid">
      {Array.from({ length: 8 }, (_, index) => (
        <div key={index} className="detail-sketch-field">
          <Sk className="sk-label" />
          <Sk className="sk-value" style={{ width: `${widthPct(index + 4, 52, 92)}%` }} />
        </div>
      ))}
    </div>
  </div>
);
